import * as Dialog from '@radix-ui/react-dialog';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';

import { BaseButtonProps } from '../../../../components/button/BaseButton';
import { ISimpleSelectItem } from '../../../../components/select/SimpleSelect';
import { useTranslation } from '../../../../contexts/translation-context';
import { OrderComplaintType, Document } from 'src/types/enums';
import { internalLogOrderComplaint } from '../../models';
import type { OrderComplaintTypeValue } from '../../endpoints/shared.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { FormDialog } from '../../../../components/dialog/FormDialog';
import { SimpleSelectField } from '../../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../../components/textarea/TextAreaField';
import { ComplaintAttachmentUpload } from './ComplaintAttachmentUpload';
import { useOrder } from './orderContext';
import type { FormikHelpers } from 'formik';

const schema = Yup.object().shape({
  type: Yup.object().required('errors.required'),
  content: Yup.string().required('errors.required'),
  attachments: Yup.array(),
});

export interface IOrderComplaintDialogProps {
  orderId: string;
  triggerColor?: BaseButtonProps['color'];
  isDisabled?: boolean;
}

interface OrderComplaintDialogValues {
  type: ISimpleSelectItem;
  content: string;
  attachments: Document[];
}

export const LogOrderComplaintDialog: React.FC<IOrderComplaintDialogProps> = (props) => {
  const { orderId, triggerColor, isDisabled } = props;
  const { i18n } = useTranslation();
  const { refreshData } = useOrder();

  const handleSubmit = async (
    values: OrderComplaintDialogValues,
    helpers: FormikHelpers<OrderComplaintDialogValues>,
  ) => {
    try {
      await internalLogOrderComplaint(orderId, {
        type: values.type.key as OrderComplaintTypeValue,
        content: values.content,
        attachmentIds: values.attachments.map((doc) => doc.id),
      });

      trackEvent('order_complaint_logged', {
        order_id: orderId,
        type: values.type.key,
        attachment_count: values.attachments.length,
      });

      helpers.resetForm();

      toast.success('Klacht gelogged');
      await refreshData();
    } catch (err) {
      captureException(err);
      console.error('Failed to log complaint', err);
      toast.error('Kon klacht niet loggen: ' + getDisplayError(err));
    }
  };

  const items: ISimpleSelectItem[] = Object.values(OrderComplaintType).map((type) => ({
    key: type,
    name: i18n(`orderComplaintType.${type}`),
  }));

  const initialValues: OrderComplaintDialogValues = useMemo(() => {
    return {
      type: items.find((v) => v.key === OrderComplaintType.Other) || items[0],
      content: '',
      attachments: [],
    };
  }, []);

  return (
    <FormDialog
      triggerColor={triggerColor}
      isDisabled={isDisabled}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      triggerText="Log klacht"
      title="Log klacht"
      submitText="Log klacht"
    >
      <Dialog.Description className="mb-4">
        Vul het formulier in om een klacht te loggen voor deze order. Je kunt bijlagen toevoegen zoals e-mails in PDF
        vorm.
      </Dialog.Description>

      <SimpleSelectField items={items} labelText="Reden" name="type" />

      <TextAreaField labelText="Bericht" name="content" />

      <ComplaintAttachmentUpload />
    </FormDialog>
  );
};
